import { useApi } from '@backstage/core-plugin-api';
import type {
  ActivityTerminalResult,
  ControlPlaneCapability,
  ControlPlaneManagementResponse,
  ControlPlaneOperationalProjection,
  UsageRecoveryAuthorizationResult,
} from '@internal/spassu-code-contract';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { controlPlaneApiRef, ControlPlaneApiError } from '../api';
import {
  MANAGEMENT_ACTIVE_POLL_INTERVAL_MS,
  MANAGEMENT_STABLE_POLL_INTERVAL_MS,
  controlPlaneManagementReducer,
  initialControlPlaneManagementState,
  isOlderManagementAttempt,
  managementPollDelay,
  type ControlPlaneManagementAction,
} from './controlPlaneManagementState';
import {
  type ManagementOperation,
  useTerminalActions,
  useUsageRecoveryAction,
} from './useControlPlaneManagementActions';

export type { ManagementOperation } from './useControlPlaneManagementActions';

export const MANAGEMENT_POLL_INTERVAL_MS = MANAGEMENT_STABLE_POLL_INTERVAL_MS;
export { MANAGEMENT_ACTIVE_POLL_INTERVAL_MS };

export interface ControlPlaneManagementView {
  activity: ControlPlaneManagementResponse['activity'] | null;
  projection: ControlPlaneOperationalProjection | null;
  loading: boolean;
  /**
   * True while the last refresh failed. The previous projection stays on screen —
   * a transient backend problem must not blank out state the user was reading
   * (IT-002).
   */
  stale: boolean;
  operation: ManagementOperation;
  error: string | null;
  announcement: string;
  usageRecoveryAuthorization: UsageRecoveryAuthorizationResult | null;
  refresh(): Promise<void>;
  finalize(): Promise<ActivityTerminalResult | null>;
  cancel(reason: string): Promise<ActivityTerminalResult | null>;
  authorizeUsageRecovery(): Promise<UsageRecoveryAuthorizationResult | null>;
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function messageFor(error: unknown): string {
  if (error instanceof ControlPlaneApiError) return error.message;
  return error instanceof Error ? error.message : 'A gestão da conexão falhou.';
}

function isAuthorizationDenied(error: unknown): boolean {
  return (
    error instanceof ControlPlaneApiError &&
    (error.statusCode === 401 || error.statusCode === 403)
  );
}

/**
 * Owner of the canonical management projection for one activity.
 *
 * The projection is the ONLY source of activity cycle, connection state, expiry
 * band and capabilities: nothing here derives expiry from a timestamp or infers
 * authorization from a visual control (ADR-009, ADR-006).
 */
/** Aborts any in-flight read and stops writes once the component unmounts. */
function useMountLifecycle(
  mounted: MutableRefObject<boolean>,
  inFlight: MutableRefObject<AbortController | null>,
) {
  useEffect(() => {
    mounted.current = true;
    const pending = inFlight;
    return () => {
      mounted.current = false;
      pending.current?.abort();
    };
  }, [mounted, inFlight]);
}

/**
 * Owns one management read. `force` distinguishes an explicit read from a
 * scheduled one: a tick must NOT cancel a read that is still running, because a
 * backend consistently slower than the poll interval would then never deliver a
 * projection at all and management would stay permanently empty. An explicit
 * refresh — or the reload after a terminal mutation — does preempt it, since the
 * caller needs the newest state now.
 */
interface ProjectionRequestRefs {
  mounted: MutableRefObject<boolean>;
  inFlight: MutableRefObject<AbortController | null>;
  requestId: MutableRefObject<number>;
  projectionRef: MutableRefObject<ControlPlaneOperationalProjection | null>;
}

function useProjectionLoad(
  activityId: string,
  api: ReturnType<typeof useApi<typeof controlPlaneApiRef.T>>,
  refs: ProjectionRequestRefs,
  dispatch: (action: ControlPlaneManagementAction) => void,
): (force?: boolean) => Promise<void> {
  const { mounted, inFlight, requestId, projectionRef } = refs;
  return useCallback(
    async (force = false) => {
      if (inFlight.current) {
        if (!force) return;
        inFlight.current.abort();
      }
      const controller = new AbortController();
      inFlight.current = controller;
      const currentRequestId = requestId.current + 1;
      requestId.current = currentRequestId;
      dispatch({ type: 'request-started', requestId: currentRequestId });
      try {
        const response = await api.getManagement(activityId, controller.signal);
        if (!mounted.current) return;
        if (
          currentRequestId === requestId.current &&
          !isOlderManagementAttempt(projectionRef.current, response.projection)
        ) {
          projectionRef.current = response.projection;
        }
        dispatch({
          type: 'request-succeeded',
          requestId: currentRequestId,
          response,
        });
      } catch (caught) {
        if (isAbort(caught) || !mounted.current) return;
        dispatch({
          type: 'request-failed',
          requestId: currentRequestId,
          message: messageFor(caught),
          authorizationDenied: isAuthorizationDenied(caught),
        });
      } finally {
        if (inFlight.current === controller) inFlight.current = null;
      }
    },
    [api, activityId, dispatch, inFlight, mounted, projectionRef, requestId],
  );
}

interface ProjectionReader {
  activity: ControlPlaneManagementResponse['activity'] | null;
  projection: ControlPlaneOperationalProjection | null;
  loading: boolean;
  stale: boolean;
  error: string | null;
  setError(next: string | null): void;
  /** `force` preempts an in-flight read; a scheduled tick must not. */
  load(force?: boolean): Promise<void>;
  mounted: { current: boolean };
}

function useProjectionReader(activityId: string): ProjectionReader {
  const api = useApi(controlPlaneApiRef);
  const [state, dispatch] = useReducer(
    controlPlaneManagementReducer,
    initialControlPlaneManagementState,
  );
  const mounted = useRef(true);
  const inFlight = useRef<AbortController | null>(null);
  const requestId = useRef(0);
  const projectionRef = useRef<ControlPlaneOperationalProjection | null>(null);
  useMountLifecycle(mounted, inFlight);

  const load = useProjectionLoad(
    activityId,
    api,
    { mounted, inFlight, requestId, projectionRef },
    dispatch,
  );

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (force = false) => {
      await load(force);
      if (cancelled) return;
      timer = setTimeout(
        () => void poll(),
        managementPollDelay(projectionRef.current),
      );
    };
    void poll(true);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [load, projectionRef]);

  return {
    activity: state.activity,
    projection: state.projection,
    loading: state.loading,
    stale: state.stale,
    error: state.error,
    setError: next => dispatch({ type: 'manual-error', message: next }),
    load,
    mounted,
  };
}

/**
 * Owner of the canonical management projection for one activity.
 *
 * Composes the projection reader with the terminal actions; both share only the
 * reload, so a failed mutation never invents state for the other half.
 */
export function useControlPlaneManagement(
  activityId: string,
): ControlPlaneManagementView {
  const reader = useProjectionReader(activityId);
  const actions = useTerminalActions(activityId, reader);
  const recovery = useUsageRecoveryAction(
    activityId,
    reader,
    actions.setOperation,
    actions.setAnnouncement,
  );
  const { activity, projection, loading, stale, error, load } = reader;

  return {
    activity,
    projection,
    loading,
    stale: stale || (projection?.stale ?? false),
    operation: actions.operation,
    error,
    announcement: actions.announcement,
    usageRecoveryAuthorization: recovery.authorization,
    refresh: () => load(true),
    finalize: actions.finalize,
    cancel: actions.cancel,
    authorizeUsageRecovery: recovery.authorize,
  };
}

/** Least-privilege default used until the projection arrives. */
export const DENIED_CAPABILITY: ControlPlaneCapability = {
  allowed: false,
  reasonCode: 'CAPABILITY_UNAVAILABLE',
  reason: 'Autorização indisponível.',
};
